import { useEffect, useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EmptyState, ProgressBar, Icon } from '@ets/design-system';
import {
  Table,
  Cell,
  type TableColumn,
  type TableRow,
  type onRowClickHandler,
  type onDoubleClickHandler,
} from '_common/components/Table';

import { useDispatch, useSelector } from '_common/hooks';
import {
  listObject,
  listRoute,
  resetList,
  updateMiniExplorer,
} from '_common/components/MiniExplorer/miniExplorerSlice';
import { updateData } from 'App/redux/appSlice';
import { getDocumentObject } from 'Editor/redux/EditorStatusSlice';

import Header from './Header';

import styles from './MiniExplorer.module.scss';

const MiniExplorer = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const data = useSelector((state) => state.app.data);
  const { current, view, selected, list, hasNextPage, operatedObjIds, totalLength, request } =
    useSelector((state) => state.miniExplorer);
  const document = useSelector(getDocumentObject);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleNewList();
    return () => {
      dispatch(updateMiniExplorer({ isCreatingFolder: false }));
    };
  }, []);

  useEffect(() => {
    dispatch(resetList());
    handleNewList();
  }, [current?.id, view]);

  const handleNewList = () => {
    if (!view && current) {
      setLoading(true);
      dispatch(listObject({ objectId: current.id })).then(() => {
        if (document) {
          dispatch(updateData({ objectId: document.id, data: { citations: document.citations } }));
        }
        setLoading(false);
      });
    } else if (view) {
      setLoading(true);
      dispatch(listRoute({ route: view })).then(() => setLoading(false));
    }
  };

  const handleClick: onRowClickHandler<Objekt> = (row) => {
    const { id, type, status } = row.original;
    if ((type === 'folder' && status !== 'broken') || type === 'space') {
      if (!operatedObjIds?.includes(id)) {
        dispatch(
          updateMiniExplorer({
            selected: selected === id ? undefined : id,
          }),
        );
      }
    }
  };
  const handleDoubleClick: onDoubleClickHandler<Objekt> = (row) => {
    const { id, type, status } = row as Objekt;

    if ((type === 'folder' && status !== 'broken') || type === 'space') {
      if (!operatedObjIds?.includes(id)) {
        dispatch(
          updateMiniExplorer({ selected: undefined, view: undefined, current: { id, type } }),
        );
      }
    }
  };

  const objectIsDisabled = (object: Objekt) => {
    return (
      operatedObjIds?.includes(object.id) ||
      (object.type !== 'folder' && object.type !== 'space') ||
      object.status === 'broken'
    );
  };

  const columns = useMemo<TableColumn<Objekt>[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        header: intl.formatMessage({ id: 'global.name' }),
        cell: (info) => {
          const object = info.row.original;

          return (
            <Cell testId={`${object.id}-name-column`} disabled={objectIsDisabled(object)}>
              <Cell.ObjectName object={object} testId={`${object.id}-object-name`} />
            </Cell>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: false,
        size: 136,
        header: intl.formatMessage({ id: 'STATUS' }),
        cell: (info) => {
          const object = info.row.original;

          return (
            <Cell testId={`${object}-status-column`} disabled={objectIsDisabled(object)}>
              <span className={styles.colStatus}>
                <Cell.ObjectStatus object={object} testId="object-status" />
              </span>
            </Cell>
          );
        },
      },
    ];
  }, []);

  const rowsData = useMemo(() => {
    return list?.map<TableRow<Objekt>>((objectId) => {
      const object = data[objectId];
      const isDisabled = objectIsDisabled(object);
      return {
        ...object,
        suffix: isDisabled
          ? {
              children: (
                <Cell testId={`${objectId}-suffix-column`}>
                  <Icon
                    size="2"
                    iconPrefix="fas"
                    icon="circle-info"
                    color="primary"
                    testId={`${objectId}-suffix`}
                  />
                </Cell>
              ),
            }
          : undefined,
      };
    });
  }, [list, data]);

  const renderList = () => {
    if (loading) {
      return (
        <div className={styles.loading}>
          <ProgressBar size="m" variant="primary-light" testId="miniExplorer">
            <FormattedMessage id="LOADING_ELEMENTS" />
          </ProgressBar>
        </div>
      );
    }
    if (list.length === 0) {
      return (
        <div className={styles.empty}>
          <EmptyState
            size="l"
            icon="folder"
            title={intl.formatMessage({ id: 'storage.browserHeader.folderEmpty' })}
            testId="empty-folder"
            description={intl.formatMessage({
              id: 'storage.modals.move.emptyFolder',
            })}
          />
        </div>
      );
    }
    return (
      <Table
        options={{ columns, data: rowsData }}
        onDoubleClick={handleDoubleClick}
        onRowClick={handleClick}
        selection={selected ? { [selected]: true } : {}}
        maxHeight="30rem"
        lazyLoading={{
          fetchNextPage: () => {
            if (!view && current) {
              dispatch(listObject({ objectId: current.id }));
            } else if (view) {
              dispatch(listRoute({ route: view }));
            }
          },
          hasNextPage,
          offset: list.length,
          isLoading: false,
          totalLength,
          totalFetched: request.offset,
        }}
        testId="objects"
        identity="mini_explorer"
        isLoading={false}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        renderFooter={() => null}
        selectable={false}
      />
    );
  };

  return (
    <div className={styles.root}>
      <Header />
      <div className={`${styles.body}`}>{renderList()}</div>
    </div>
  );
};

export default MiniExplorer;
